import type { DiamondType, Hazard, VaultId } from "../domain/models";
import type { LevelObjective } from "../domain/LevelObjective";

export interface CampaignLevel {
  id: number;
  vault: VaultId;
  vaultName: string;
  targetDiamonds: number;
  startTime: number;
  hazards: number;
  hazardSpeed: number;
  hazardPenalty: number;
  timeRewardScale: number;
  scoreScale: number;
  comboWindow: number;
  spawnMargin: number;
  master: boolean;
  objective: LevelObjective;
  difficulty: "Easy" | "Medium" | "Hard" | "Expert" | "Master";
  allowedHazards: Array<NonNullable<Hazard["type"]>>;
  rewardPool: DiamondType[];
  masterRule?: "laser_grid" | "minefield" | "mixed_lockdown";
}

const chapters: Array<{ vault: VaultId; name: string }> = [
  { vault: "street", name: "Street Safes" },
  { vault: "bank", name: "Bank Vaults" },
  { vault: "casino", name: "Casino Floor" },
  { vault: "museum", name: "Museum Heist" },
  { vault: "server", name: "Server Vault" },
  { vault: "ghost", name: "Ghost Vault" },
];

const hazardLessons: Array<Array<NonNullable<Hazard["type"]>>> = [
  [],
  ["drone"],
  ["tracker"],
  ["laser"],
  ["laser"],
  ["jammer"],
  ["mine"],
  ["hunter"],
  ["drone", "tracker"],
  ["drone", "tracker", "laser"],
  ["drone", "tracker", "mine"],
  ["drone", "jammer"],
  ["tracker", "hunter"],
  ["laser", "mine"],
  ["drone", "tracker", "jammer"],
  ["laser", "mine"],
  ["drone", "tracker", "hunter"],
  ["jammer", "mine"],
  ["laser", "hunter"],
  ["drone", "tracker", "laser", "mine"],
  ["laser", "mine"],
  ["drone", "tracker", "jammer", "hunter"],
  ["laser", "mine", "hunter"],
  ["drone", "tracker", "laser", "jammer"],
  ["tracker", "mine", "hunter"],
  ["laser", "mine", "hunter"],
  ["drone", "tracker", "laser", "jammer", "mine"],
  ["tracker", "jammer", "hunter", "mine"],
  ["drone", "laser", "mine", "hunter"],
  ["drone", "tracker", "laser", "jammer", "mine", "hunter"],
  ["laser", "mine", "hunter", "jammer"],
];

const objectiveFor = (id: number, chapterIndex: number, master: boolean): LevelObjective => {
  if (id === 1) return { type: "diamonds", target: 6 };
  if (master || id % 3 === 1) {
    return {
      type: "diamonds",
      target: 8 + Math.ceil(id * 0.72) + chapterIndex * 2 + (master ? 3 : 0),
    };
  }
  if (id % 3 === 2) {
    return {
      type: "combo",
      target: Math.min(12, 4 + chapterIndex + Math.floor(id / 8)),
    };
  }
  return {
    type: "rare",
    target: Math.min(8, 2 + chapterIndex + Math.floor(id / 9)),
  };
};

const HAZARD_LABELS: Record<NonNullable<Hazard["type"]>, string> = {
  drone: "drone",
  tracker: "tracker",
  laser: "laser gate",
  jammer: "jammer field",
  mine: "pulse mine",
  hunter: "hunter",
};

const DIAMOND_LABELS: Record<DiamondType, string> = {
  SHARD: "Shard",
  CUT_DIAMOND: "Cut diamond",
  FLAWLESS_DIAMOND: "Flawless diamond",
  SHIELD_DIAMOND: "Shield diamond",
  CLEAN_DIAMOND: "Clean diamond",
  FREEZE_DIAMOND: "Freeze diamond",
  TIME_DIAMOND: "Time diamond",
  SYNDICATE_GEM: "Syndicate gem",
  CROWN_JEWEL: "Crown jewel",
};

const rewardPoolFor = (id: number): DiamondType[] => {
  const pool: DiamondType[] = ["SHARD", "CUT_DIAMOND", "FLAWLESS_DIAMOND"];
  if (id >= 4) pool.push("SHIELD_DIAMOND");
  if (id >= 7) pool.push("CLEAN_DIAMOND");
  if (id >= 10) pool.push("FREEZE_DIAMOND");
  if (id >= 13) pool.push("TIME_DIAMOND");
  if (id >= 16) pool.push("SYNDICATE_GEM");
  if (id >= 24) pool.push("CROWN_JEWEL");
  return pool;
};

export const CAMPAIGN_LEVELS: CampaignLevel[] = Array.from({ length: 150 }, (_, index) => {
  const id = index + 1;
  const chapterIndex = Math.floor(index / 10);
  const chapter = chapters[chapterIndex % chapters.length];
  const posInChapter = ((id - 1) % 10) + 1;
  const master = posInChapter === 10;
  const difficulty: CampaignLevel["difficulty"] = master
    ? "Master"
    : posInChapter === 6
      ? "Hard"
      : chapterIndex <= 2
        ? "Easy"
        : chapterIndex <= 7
          ? "Medium"
          : "Expert";

  const chapterPressure = chapterIndex * 0.15;
  const objective = objectiveFor(id, chapterIndex, master);
  const allowedHazards = hazardLessons[index % hazardLessons.length] ?? hazardLessons[0];
  const guidedLesson = id >= 2 && id <= 8;
  const hazardCount = id === 1
    ? 0
    : guidedLesson
      ? 1
      : id <= 12
        ? Math.min(2, Math.max(1, Math.ceil(allowedHazards.length * 0.65)))
        : Math.min(7, Math.max(2, Math.ceil(allowedHazards.length * 0.8) + Math.floor(chapterIndex / 2) + (master ? 1 : 0)));
  const hazardSpeed = id <= 8
    ? 24 + Math.max(0, id - 2) * 1.4
    : id <= 30
      ? 34 + (id - 9) * 1.2
      : 60 + Math.min(40, (id - 30) * 0.5);

  return {
    id,
    vault: chapter.vault,
    vaultName: chapter.name,
    targetDiamonds: objective.type === "diamonds" ? objective.target : 8 + Math.ceil(id * 0.5) + chapterIndex,
    startTime: Math.max(12, 27 - chapterIndex * 0.8 - (master ? 1 : 0)),
    hazards: hazardCount,
    hazardSpeed: Math.round(hazardSpeed),
    hazardPenalty: Math.min(3.8, 0.9 + id * 0.02 + chapterPressure),
    timeRewardScale: Math.max(0.5, 1 - chapterIndex * 0.03 - (master ? 0.04 : 0)),
    scoreScale: 1 + (id - 1) * 0.03 + chapterPressure,
    comboWindow: Math.max(0.9, 1.85 - chapterIndex * 0.05 - (master ? 0.05 : 0)),
    spawnMargin: Math.min(122, 58 + id * 0.4 + chapterIndex * 2),
    master,
    objective,
    difficulty,
    allowedHazards,
    rewardPool: rewardPoolFor(id),
    masterRule: master
      ? (id % 30 === 10 ? "laser_grid" : id % 30 === 20 ? "minefield" : "mixed_lockdown")
      : undefined,
  };
});

export const campaignLevel = (id: number): CampaignLevel => {
  const level = CAMPAIGN_LEVELS[id - 1];
  if (!level) throw new Error(`Unknown campaign level ${id}`);
  return level;
};

export const masterRuleName = (rule: CampaignLevel["masterRule"]): string => {
  if (rule === "laser_grid") return "Laser Grid";
  if (rule === "minefield") return "Pulse Minefield";
  if (rule === "mixed_lockdown") return "Mixed Lockdown";
  return "";
};

export const difficultyTagFor = (level: CampaignLevel): string =>
  level.master ? "Master Vault" : `${level.difficulty} Run`;

export const securityBriefFor = (level: CampaignLevel): string => {
  if (level.hazards === 0) return "Training: no security";
  if (level.id >= 2 && level.id <= 8) {
    return `New: slow ${HAZARD_LABELS[level.allowedHazards[0]]}`;
  }
  const kinds = level.allowedHazards.slice(0, 2).map((hazard) => HAZARD_LABELS[hazard]).join(" + ");
  return `Security ${level.hazards}: ${kinds}`;
};

export const rewardUnlockFor = (level: CampaignLevel): string | undefined => {
  if (level.id === 1) return undefined;
  const previous = level.id > 1 ? CAMPAIGN_LEVELS[level.id - 2]?.rewardPool ?? [] : [];
  const unlocked = level.rewardPool.find((reward) => !previous.includes(reward));
  return unlocked ? `Unlock: ${DIAMOND_LABELS[unlocked]}` : undefined;
};

export const starsForTime = (remaining: number, startTime: number): number => {
  if (remaining >= startTime * 0.5) return 3;
  if (remaining >= startTime * 0.25) return 2;
  return 1;
};

export interface EndlessDifficulty {
  stage: number;
  hazardTarget: number;
  hazardSpeedScale: number;
  timerDrainScale: number;
  timeRewardScale: number;
  comboWindow: number;
  spawnMargin: number;
}

export const endlessDifficulty = (elapsedSeconds: number): EndlessDifficulty => {
  const stage = Math.floor(elapsedSeconds / 30) + 1;
  return {
    stage,
    hazardTarget: Math.min(8, 1 + Math.floor((stage - 1) / 2)),
    hazardSpeedScale: Math.pow(1.1, stage - 1),
    timerDrainScale: 1 + (stage - 1) * 0.05,
    timeRewardScale: Math.max(0.5, 1 - (stage - 1) * 0.1),
    comboWindow: Math.max(0.9, 1.5 - Math.floor(elapsedSeconds / 60) * 0.2),
    spawnMargin: Math.min(110, 65 + (stage - 1) * 6),
  };
};
